/**
 * 블록 텍스트 파싱에 사용되는 정규식/상수 모음
 *
 * 모든 패턴을 한 곳에 모아 가독성과 유지보수성을 높인다.
 */

// 차트 타입 (ChartType 타입은 model/block.types.ts에서 이 배열로부터 도출)
export const CHART_TYPES = [
  'bar-vertical',
  'bar-horizontal',
  'line',
  'area',
  'pie',
  'doughnut',
] as const;

// 비디오 확장자 목록
export const videoExtensions = [
  'mp4',
  'webm',
  'mov',
  'avi',
  'mkv',
  'flv',
  'wmv',
  'm4v',
  'ogv',
];

// 이미지 마커가 있는 줄에서 마지막 ": URL"까지만 이미지로 본다.
export const imgPattern =
  /\[\^\^\^img\](?:([^\n]*?)\s*\(img_size:\s*(.*?)\)\s*:\s*(https?:\/\/[^\s\n]+)|([^\n]*):\s*(https?:\/\/[^\s\n]+))/;

// 다음 마커가 바로 붙어도 앞 링크의 라벨/URL로 먹히지 않도록 마커 경계를 먼저 끊는다.
const nextMarkerBoundaryPattern =
  String.raw`\[\^\^\^(?:img|link|table|chart)\]|\[A_TAG\]|\[\/A_TAG\]`;
const markerSafeTextPattern = String.raw`(?:(?!${nextMarkerBoundaryPattern})[^\n])*?`;
const markerSafeUrlPattern = String.raw`https?:\/\/(?:(?!${nextMarkerBoundaryPattern})[^\s\n])+`;

// 링크 마커가 있는 줄에서 마지막 ": URL"까지만 링크로 본다.
export const linkPattern =
  new RegExp(
    String.raw`\[\^\^\^link\](?:(${markerSafeTextPattern}):\s*(${markerSafeUrlPattern})|(${markerSafeTextPattern})(${markerSafeUrlPattern}))`
  );

// 메일 링크 마커 패턴: [^^^link]표시텍스트: mailto:주소 (A_TAG 내부에서 mail 노드 분류에 사용)
export const mailLinkPattern = /\[\^\^\^link\]([^\n]*):\s*mailto:([^\s\n]+)/;

// A태그 패턴: [A_TAG]내용[/A_TAG]
export const aTagPattern = /\[A_TAG\](.*?)\[\/A_TAG\]/s;

// 테이블 마커 패턴: [^^^table] (선택적 [테이블 내용] 및 여는 괄호)
export const tableMarkerPattern =
  /\[\^\^\^table\]\s*(?:\[테이블 내용\]\s*)?(?:\()?/i;

// 테이블 이미지 URL 패턴: [^^^img]테이블 이미지: URL
export const imgTableUrlPattern = /\[\^\^\^img\]테이블 이미지:\s*(\S+)/i;

// 테이블 PlainText 패턴: [^^^table] 뒤에 <table이 아닌 텍스트가 오는 경우
export const tablePlainTextPattern =
  /\[\^\^\^table\]\s*(?:\[테이블 내용\]\s*)?(?:\()?(?!\s*(?:\()?<table)([\s\S]*?)(?=\[\^\^\^img\]테이블 이미지:|\[\^\^\^img\]|\[\^\^\^link\]|\[\^\^\^table\]|\[A_TAG\]|$)/i;

// 테이블 PlainText + 이미지 결합 패턴
export const tablePlainTextWithImagePattern =
  /\[\^\^\^table\]\s*(?:\[테이블 내용\]\s*)?(?:\()?(?!\s*(?:\()?<table)([\s\S]*?)\[\^\^\^img\]테이블 이미지:\s*(\S+)/i;

// 차트 마커 패턴: [^^^chart] (선택적 "차트 내용:")
export const chartMarkerPattern = /\[\^\^\^chart\]\s*(?:차트 내용:\s*)?/i;

// Raw URL 패턴: 줄 전체가 http(s) URL인지 판별
export const rawUrlPattern = /^https?:\/\/\S+$/;
